import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronRight, ShieldCheck } from "lucide-react";

import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { getTotpStatus } from "@/services/totpService";

/**
 * The professional's second factor, on their profile — the web half of the
 * professional app's security card (apps/mobile-pros profile `SecurityCard`).
 *
 * WHY IT BELONGS HERE. Enrollment was reachable only by being REFUSED: the
 * apiClient's redirect on the enrollment code, or the blocking gate when
 * AUTH_TOTP_ENFORCED is on. A professional who wanted to turn two-step
 * verification on deliberately — or simply to check whether it was on — had
 * nowhere to look, while the native app answered both at a glance.
 *
 * TWO STATES, the mobile card's. Unconfigured offers the action; configured
 * states the fact as a value with a tinted pill beside it and demotes the way
 * in to an outline button — once it is on, this card's job is to answer "is it
 * on?", not to keep advertising an action already taken.
 *
 * THE STATUS IS NOT RE-DERIVED HERE. Same query key as `useTwoFactorGate`, so
 * the two never disagree and the sweeping invalidation the enrollment gate
 * fires on success refreshes this card too.
 *
 * WHILE IT LOADS, NOTHING IS CLAIMED. The native card renders unknown as
 * unconfigured; here the state line is a skeleton instead, because on a wide
 * card the "Inactiva" pill would be read before it flipped — and telling a
 * professional their 2FA is off when it is on is the one wrong answer this
 * card can give.
 *
 * Spec 5d9018f9, control C-5.
 */
export function MyTwoFactorCard(): JSX.Element {
  const { t } = useTranslation();

  const { data: status, isPending } = useQuery({
    queryKey: ["me", "totp", "status"],
    queryFn: getTotpStatus,
    staleTime: 30_000,
  });

  const enrolled = status?.enrolled === true;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
          {t("profile.security.title")}
        </CardTitle>
        <CardDescription>{t("profile.security.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {isPending ? (
          <Skeleton className="h-5 w-56" />
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground">
              {t("profile.security.twoFactor")}
            </span>
            <StatusBadge variant={enrolled ? "success" : "warning"}>
              {t(enrolled ? "profile.security.active" : "profile.security.inactive")}
            </StatusBadge>
          </div>
        )}

        {/* The count is the one thing an enrolled professional can act on
            BEFORE they are locked out — there is no endpoint that issues more,
            so running out means re-enrolling with an admin's help. */}
        {enrolled && status != null ? (
          <p className="text-xs text-muted-foreground">
            {t("security.twoFactor.recoveryRemaining", {
              count: status.unusedRecoveryCodes,
            })}
          </p>
        ) : null}

        <Button variant={enrolled ? "outline" : "default"} asChild>
          <Link to="/security/two-factor" className="inline-flex items-center">
            {t(enrolled ? "profile.security.manage" : "profile.security.setUp")}
            <ChevronRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
